import { makeAuthenticatedRequest } from './api';

/**
 * Knowledge Base API Service
 * All API calls for knowledge base: queries, clusters, analytics
 */

const knowledgeBaseApi = {
  // ════════════════════════════════════════════════════════
  // KNOWLEDGE QUERY
  // ════════════════════════════════════════════════════════
  query: {
    smartSearch: async (queryText, options = {}) => {
      const payload = {
        operation: 'smart_search', query: queryText,
        agent_id: options.agent_id, max_results: options.max_results || 20,
        min_credibility: options.min_credibility, min_trust: options.min_trust,
        min_importance: options.min_importance,
        include_source: options.include_source !== undefined ? options.include_source : true,
        include_quality_metrics: options.include_quality_metrics !== undefined ? options.include_quality_metrics : true,
        include_calibration: options.include_calibration || false,
        include_nodes: options.include_nodes || false,
      };
      return knowledgeBaseApi._post('/knowledge/knowledge_query', payload);
    },
    getLearning: async (learningId, options = {}) => {
      const payload = {
        operation: 'get_learning', learning_id: learningId,
        include_source: options.include_source !== undefined ? options.include_source : true,
        include_quality_metrics: options.include_quality_metrics || false,
        include_calibration: options.include_calibration || false,
        include_clusters: options.include_clusters || false,
        include_nodes: options.include_nodes || false,
        include_embedding: options.include_embedding || false,
      };
      return knowledgeBaseApi._post('/knowledge/knowledge_query', payload);
    },
    search: async (filters = {}) => knowledgeBaseApi._post('/knowledge/knowledge_query', { operation: 'search', ...filters }),
    searchByText: async (searchText, options = {}) => knowledgeBaseApi._post('/knowledge/knowledge_query', { operation: 'search_by_text', search_text: searchText, agent_id: options.agent_id, max_results: options.max_results || 20 }),
    getRelated: async (learningId, maxResults = 10) => knowledgeBaseApi._post('/knowledge/knowledge_query', { operation: 'related', learning_id: learningId, max_results: maxResults }),
    getStats: async (agentId) => knowledgeBaseApi._post('/knowledge/knowledge_query', { operation: 'stats', agent_id: agentId }),
  },

  // ════════════════════════════════════════════════════════
  // CLUSTERS
  // ════════════════════════════════════════════════════════
  clusters: {
    get: async (clusterId, options = {}) => knowledgeBaseApi._post('/knowledge/cluster_query', {
      operation: 'get_cluster', cluster_id: clusterId,
      include_members: options.include_members || false,
      include_centroid: options.include_centroid || false,
      include_scores: options.include_scores !== undefined ? options.include_scores : true,
      include_source_breakdown: options.include_source_breakdown || false,
    }),
    list: async (agentId, options = {}) => knowledgeBaseApi._post('/knowledge/cluster_query', {
      operation: 'list_clusters', agent_id: agentId,
      status: options.status || 'active', min_member_count: options.min_member_count,
      max_member_count: options.max_member_count, min_coherence_score: options.min_coherence_score,
      min_quality_score: options.min_quality_score, cluster_category: options.cluster_category,
      hierarchy_level: options.hierarchy_level, sort_by: options.sort_by || 'member_count',
      sort_direction: options.sort_direction || 'desc', page: options.page || 1, page_size: options.page_size || 50,
    }),
    search: async (query, agentId, maxResults = 20) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'search_clusters', query, agent_id: agentId, max_results: maxResults }),
    compare: async (clusterIds) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'compare_clusters', cluster_ids: clusterIds }),
    getMembers: async (clusterId, options = {}) => knowledgeBaseApi._post('/knowledge/cluster_query', {
      operation: 'get_members', cluster_id: clusterId, sort_by: options.sort_by || 'importance',
      sort_direction: options.sort_direction || 'desc', page: options.page || 1, page_size: options.page_size || 50,
      min_credibility: options.min_credibility, learning_classification: options.learning_classification,
    }),
    getMembershipOverlap: async (id1, id2) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_membership_overlap', cluster_id_1: id1, cluster_id_2: id2 }),
    getHierarchy: async (clusterId) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_hierarchy', cluster_id: clusterId }),
    getFullTree: async (agentId) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_full_tree', agent_id: agentId }),
    getStats: async (agentId) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_stats', agent_id: agentId }),
    getScoreDistribution: async (clusterId) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_score_distribution', cluster_id: clusterId }),
    getSourceBreakdown: async (clusterId) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_source_breakdown', cluster_id: clusterId }),
    getClassificationBreakdown: async (clusterId) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_classification_breakdown', cluster_id: clusterId }),
    getCalibrationSummary: async (clusterId, days = 30) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_calibration_summary', cluster_id: clusterId, days }),
    getConflictSummary: async (clusterId, days = 30) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_conflict_summary', cluster_id: clusterId, days }),
    getTimeline: async (clusterId, interval = 'day', days = 90) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_timeline', cluster_id: clusterId, interval, days }),
    getHealthReport: async (clusterId) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_health_report', cluster_id: clusterId }),
    getEvolution: async (clusterId) => knowledgeBaseApi._post('/knowledge/cluster_query', { operation: 'get_evolution', cluster_id: clusterId }),
  },

  // ════════════════════════════════════════════════════════
  // ANALYTICS
  // ════════════════════════════════════════════════════════
  analytics: {
    getDashboard: async (agentId, days = 30) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'dashboard', agent_id: agentId, time_period_days: days }),
    getTrends: async (agentId, days = 30, groupBy = 'day') => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'trends', agent_id: agentId, time_period_days: days, group_by: groupBy }),
    getAgentStats: async (agentId, days = 30) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'agent_stats', agent_id: agentId, time_period_days: days }),
    getClusterEvolution: async (agentId) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'cluster_evolution', agent_id: agentId }),
    getNodeEvolution: async (days = 90) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'node_evolution', time_period_days: days }),
    getQualityDistribution: async (agentId) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'quality_distribution', agent_id: agentId }),
    getUsageHeatmap: async (agentId, days = 30) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'usage_heatmap', agent_id: agentId, time_period_days: days }),
    getConflictAnalysis: async (days = 90) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'conflict_analysis', time_period_days: days }),
    getGrowthProjection: async (agentId, days = 90) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'growth_projection', agent_id: agentId, time_period_days: days }),
    getCalibrationAnalytics: async (agentId, days = 30) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'calibration_analytics', agent_id: agentId, time_period_days: days }),
    getSourceAnalytics: async (agentId, days = 90, sourceId) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'source_analytics', agent_id: agentId, time_period_days: days, source_id: sourceId }),
    getScoreEvolution: async (agentId, days = 90, groupBy = 'week') => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'score_evolution', agent_id: agentId, time_period_days: days, group_by: groupBy }),
    getClassificationBreakdown: async (agentId) => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'classification_breakdown', agent_id: agentId }),
    getHealthCheck: async () => knowledgeBaseApi._post('/knowledge/analytics_views', { operation: 'health_check' }),
  },

  // ════════════════════════════════════════════════════════
  // INTERNAL HELPERS
  // ════════════════════════════════════════════════════════
  _post: async (endpoint, payload) => {
    try {
      const cleaned = {};
      Object.entries(payload).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') cleaned[k] = v; });
      const response = await makeAuthenticatedRequest('POST', endpoint, cleaned);
      return knowledgeBaseApi._parseResponse(response);
    } catch (error) {
      console.error(`Error calling ${endpoint}:`, error);
      throw error;
    }
  },

  _parseResponse: (response) => {
    let rawData = response;
    if (response && response.data !== undefined) rawData = response.data;
    let parsed = rawData;
    if (rawData && typeof rawData.body === 'string') {
      try { parsed = JSON.parse(rawData.body); }
      catch (e) { throw new Error('Failed to parse API response: ' + e.message); }
    } else if (rawData && typeof rawData.body === 'object' && rawData.body !== null) {
      parsed = rawData.body;
    }
    if (rawData && rawData.statusCode && rawData.statusCode >= 400) {
      throw new Error(parsed?.error || parsed?.message || `API returned ${rawData.statusCode}`);
    }
    return parsed;
  }
};

export const fetchKnowledgeParameters = async () => {
  try {
    const response = await makeAuthenticatedRequest('POST', '/knowledge/configure-knowledge/', {
      action: 'list', parameters: { page: 1, page_size: 500, include_overrides: false }
    });
    let rawData = response;
    if (response && response.data !== undefined) rawData = response.data;
    let parsed = rawData;
    if (rawData && typeof rawData.body === 'string') {
      try { parsed = JSON.parse(rawData.body); } catch (e) { throw new Error('Parse error: ' + e.message); }
    } else if (rawData && typeof rawData.body === 'object' && rawData.body !== null) { parsed = rawData.body; }
    if (rawData && rawData.statusCode && rawData.statusCode !== 200) throw new Error(`Status ${rawData.statusCode}`);
    return { parameters: parsed?.parameters || [], categories: parsed?.categories || [], pagination: parsed?.pagination || {} };
  } catch (error) { console.error('Error fetching knowledge parameters:', error); throw error; }
};

export default knowledgeBaseApi;
